import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { useKdsRouting } from "./useKdsRouting";
import { notificationSound } from "@/lib/notification-sound";

/**
 * Watches for new/updated orders and automatically creates KDS ticket items.
 * Should be mounted once in KdsLayout.
 */
export function useKdsRealtime() {
  const { activeRestaurant } = useRestaurantContext();
  const { createKdsItemsForOrder } = useKdsRouting(activeRestaurant?.id || null);
  const processedOrders = useRef<Set<string>>(new Set());
  const initialLoadDone = useRef(false);

  useEffect(() => {
    if (!activeRestaurant) return;

    initialLoadDone.current = false;

    const processOrder = async (orderId: string, playSound = true) => {
      if (processedOrders.current.has(orderId)) return;
      processedOrders.current.add(orderId);

      // Fetch order items
      const { data: items } = await supabase
        .from("order_items")
        .select("id, order_id, product_id, product_name, quantity, notes, combo_id, combo_selections")
        .eq("order_id", orderId);

      if (items && items.length > 0) {
        await createKdsItemsForOrder(orderId, items as any);
        if (playSound && initialLoadDone.current) {
          notificationSound.playNewOrder();
        }
      }

      // Allow re-processing after 5s (for updates)
      setTimeout(() => processedOrders.current.delete(orderId), 5000);
    };

    // Listen for new orders
    const channel = supabase
      .channel(`kds-orders-${activeRestaurant.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "orders",
          filter: `restaurant_id=eq.${activeRestaurant.id}`,
        },
        (payload) => {
          const order = payload.new as any;
          if (order.status === 'pending' || order.status === 'preparing') {
            processOrder(order.id);
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "orders",
          filter: `restaurant_id=eq.${activeRestaurant.id}`,
        },
        (payload) => {
          const order = payload.new as any;
          if (order.status === 'preparing') {
            processOrder(order.id);
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "order_items",
        },
        (payload) => {
          const item = payload.new as any;
          if (item.order_id) {
            processedOrders.current.delete(item.order_id);
            processOrder(item.order_id);
          }
        }
      )
      .subscribe();

    // Also process existing active orders on mount
    const loadExisting = async () => {
      const { data: orders } = await supabase
        .from("orders")
        .select("id")
        .eq("restaurant_id", activeRestaurant.id)
        .in("status", ["pending", "preparing", "ready"])
        .order("created_at", { ascending: false })
        .limit(100);

      if (orders) {
        for (const order of orders) {
          await processOrder(order.id, false);
        }
      }
      initialLoadDone.current = true;
    };
    loadExisting();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeRestaurant, createKdsItemsForOrder]);
}
